<script setup>
import { useValidation, useFormContext } from '/app/composables/form';
import { middleTruncate } from '@bignerve/ui-utils';
import { cn } from '/app/utils/helpers';
import { ref, reactive, computed, useTemplateRef } from 'vue';
import { api } from '/app/plugins/services';
import { sentry } from '/app/plugins/sentry';
import NIcon from '/app/components/NIcon.vue';
import NAlert from '/app/components/NAlert.vue';

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    name: { type: String, default: null },
    label: { type: String, default: null },
    hint: { type: String, default: null },
    fieldName: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    forceError: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    modelValue: {
        type: String,
        default: '',
    },
    accept: {
        type: Array,
        default: () => [],
    },
    maxWidth: {
        type: Number,
        default: 8000,
    },
    maxHeight: {
        type: Number,
        default: 8000,
    },
    buttonText: {
        type: String,
        default: 'Browse',
    },
    truncate: {
        type: Number,
        default: 30,
    },
    border: {
        type: Boolean,
        default: true,
    },
    maxSize: {
        type: Number,
        default: 1024 * 1024 * 10,
    },
    draggablePanel: {
        type: Boolean,
        default: true,
    },
    class: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['update:modelValue', 'loaded', 'change']);

const form = useFormContext();
const { invalid, error, attrs, clearErrors, setError, focus, target } = useValidation(props);

const fileUpload = useTemplateRef('fileUpload');

const file = ref(null);
const busy = ref(false);
const hasError = ref(false);
const imageLoaded = ref(false);
const draging = ref(false);
const progress = ref(0);
const image = reactive({
    bytes: null,
    width: null,
    height: null,
});

const headers = computed(() => ({
    'Content-Type': file.value?.type,
}));

const title = computed(() => file.value ? file.value.name : 'Upload');

const validDimension = computed(() => [
    image.width <= props.maxWidth,
    image.height <= props.maxHeight,
].every(Boolean));

const validSize = computed(() => image.bytes <= props.maxSize);

function handle(event) {
    hasError.value = false;
    file.value = event.target.files.item(0);
    loadImage(file.value);
}

async function upload(f) {
    busy.value = true;

    try {
        const { data: { url } } = await api.resource.uploadImage(
            file.value, { type: file.value.type }, headers.value, (p) => {
                progress.value = p;
            },
        );

        emit('update:modelValue', url);
        emit('change', { url });

        file.value = null;
        fileUpload.value.value = null;
        progress.value = 0;
    } catch (err) {
        busy.value = false;
        hasError.value = true;
        sentry.withScope((scope) => {
            scope.setTag('event', 'upload-image');
            scope.setContext('image', {
                size: f.size,
                type: f.type,
                name: f.name,
                width: image.width,
                height: image.height,
            });
            sentry.captureException(err);
        });
    } finally {
        busy.value = false;
    }
}

function loadImage(f) {
    if (!f || f.type.indexOf('image/') !== 0) {
        return;
    }

    const reader = new FileReader();

    reader.readAsDataURL(f);
    reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
            image.width = img.width;
            image.height = img.height;
            image.bytes = f.size;
            imageLoaded.value = true;

            emit('loaded', { img });

            if (validDimension.value && validSize.value) {
                upload(f);
            }
        };
        img.src = e.target.result;
    };

    reader.onerror = (e) => {
        console.error(e);
    };
}

function dragover(event) {
    event.preventDefault();
    draging.value = true;
}

function dragleave(event) {
    event.preventDefault();
    draging.value = false;
}

function drop(event) {
    event.preventDefault();
    file.value = event.dataTransfer.files[0];
    loadImage(file.value);
    draging.value = false;
}

function paste(event) {
    event.preventDefault();
    const items = event.clipboardData.items;

    for (let i = 0; i < items.length; i++) {
        const f = items[i].getAsFile();
        file.value = f;
        loadImage(f);
    }
}
</script>

<template>
    <slot />
    <NAlert :open="hasError" type="danger" class="mt-3 mb-0" closable>
        Error processing the image.
    </NAlert>
    <NAlert :open="!validDimension" type="danger" class="mt-3 mb-0" closable>
        Maximum width and height dimension for upload image is {{ maxWidth }} x {{ maxHeight }}.
    </NAlert>
    <NAlert :open="!validSize" type="danger" class="mt-3 mb-0" closable>
        Maximum file size for upload image is {{ maxSize / 1024 / 1024 }} MB.
    </NAlert>
    <div :class="cn('flex w-full px-4 md:px-6 py-4 my-6 md:rounded-lg overflow-hidden context:animation-bounce', border ? 'md:border-dashed md:border-2 focus:border-dust-300' : null, $props.class)" @dragover="dragover" @dragleave="dragleave" @drop="drop" @paste="paste" >
        <div class="flex w-full flex-col sm:flex-row sm:items-center">
            <label class="flex-1 flex items-center relative flex-col sm:flex-row gap-4 rounded-lg">
                <div class="flex w-full items-center border border-dust-300 py-2 px-4 bg-white mr-2 rounded-lg overflow-hidden whitespace-nowrap max-w-90" :title="title">
                    <NIcon as="image-regular" class="text-dust-600 text-lg mr-3" />
                    <span class="" :class="{ 'text-dust-400': !file }">
                        {{ middleTruncate(title, truncate) }}
                    </span>
                </div>
                <div :class="{ 'hover:bg-dust-600': !busy }" class="relative cursor-pointer bg-dust-500 inline-flex overflow-hidden sm:max-w-40 items-center justify-center p-2 px-6 rounded-lg shadow border bg-dust text-white transition ease-in-out duration-300 cursor-pointer whitespace-nowrap w-full">
                    <div class="absolute h-full bg-dust-600 bottom-0 left-0 transition-all duration-500 easy-in-out" :style="{ width: `${progress}%`,  }"></div>
                    <span class="z-[1]">{{ busy ? "Uploading" : buttonText }}</span>
                    <svg v-if="busy" class="animate-spin !h-5 !w-5 ml-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
                <input
                    v-bind="attrs"
                    type="file"
                    class="custom-file-upload__input absolute"
                    ref="fileUpload"
                    :accept="accept.join(', ')"
                    :disabled="form.busy || busy"
                    @change="handle"
                />
            </label>
            <slot v-if="draggablePanel" name="draging" :draging="draging">
                <div class="flex-1 text-dust items-center whitespace-nowrap justify-center flex-col space-y-3 py-8 px-2 m-6 w-full hidden md:flex text-center">
                    <span v-if="draging">drop your image here</span>
                    <span v-else>Drag or paste your image here</span>
                    <svg :class="draging ? 'animation-bounce' : 'target:animation-bounce'" class="w-6 h-6" fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                    </svg>
                </div>
            </slot>
        </div>
    </div>
</template>

<style lang="scss">
.context\:animation-bounce {
    .none\:animation-bounce {
        &:hover .target\:animation-bounce {
            @apply animate-bounce;
        }
    }
    &:hover .target\:animation-bounce {
        @apply animate-bounce;
    }
}
</style>
